import { Injectable } from '@angular/core';
import { IUserAuthorization } from '@shared/interfaces/user/user-authorization';
import { ApiService } from '@shared/services/api.service';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';

export interface PreviewContext {
  token: string;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class PreviewService {
  private readonly storageKey = 'preview_session';

  private readonly activeSubject = new BehaviorSubject<boolean>(
    this.hasValidPreview()
  );

  readonly active$ = this.activeSubject.asObservable();

  constructor(private readonly apiService: ApiService) {}

  start(userId: string, organizationId: string): Observable<void> {
    return this.apiService.createPreviewToken(userId, organizationId).pipe(
      tap((response) => {
        this.store(response);

        this.activeSubject.next(true);
      }),
      map(() => void 0)
    );
  }

  stop(): void {
    this.clear();

    this.activeSubject.next(false);
  }

  isActive(): boolean {
    return this.hasValidPreview();
  }

  getToken(): string | null {
    const context = this.getContext();

    if (!context) {
      return null;
    }

    if (context.expiresAt <= Date.now()) {
      this.stop();

      return null;
    }

    return context.token;
  }

  getExpiresAt(): number | null {
    const context = this.getContext();

    if (!context) {
      return null;
    }

    if (context.expiresAt <= Date.now()) {
      this.stop();

      return null;
    }

    return context.expiresAt;
  }

  getRemainingTime(): number {
    const expiresAt = this.getExpiresAt();

    if (!expiresAt) {
      return 0;
    }

    return Math.max(0, expiresAt - Date.now());
  }

  private store(response: IUserAuthorization): void {
    const context: PreviewContext = {
      token: response.accessToken,
      expiresAt: Date.now() + response.expiresIn * 1000,
    };

    sessionStorage.setItem(this.storageKey, JSON.stringify(context));
  }

  private getContext(): PreviewContext | null {
    const raw = sessionStorage.getItem(this.storageKey);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as PreviewContext;
    } catch {
      this.clear();

      return null;
    }
  }

  private hasValidPreview(): boolean {
    const context = this.getContext();

    if (!context) {
      return false;
    }

    if (context.expiresAt <= Date.now()) {
      this.clear();

      return false;
    }

    return true;
  }

  private clear(): void {
    sessionStorage.removeItem(this.storageKey);
  }
}
